import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import { cn } from "../../../../lib/utils";

type YearPickerProps = {
    value: number;
    onChange?: (value: number) => void;
    minYear?: number;
    maxYear?: number;
    yearsPerPage?: number;
    className?: string;
};

function YearPicker({ value, onChange, minYear, maxYear, yearsPerPage = 12, className }: YearPickerProps) {
    const [open, setOpen] = useState(false);
    const [rangeStart, setRangeStart] = useState(() => Math.floor(value / yearsPerPage) * yearsPerPage);

    const years = useMemo(
        () => Array.from({ length: yearsPerPage }, (_, i) => rangeStart + i),
        [rangeStart, yearsPerPage],
    );

    const canGoPrev = minYear == null || rangeStart - 1 >= minYear;
    const canGoNext = maxYear == null || rangeStart + yearsPerPage <= maxYear;

    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (next) setRangeStart(Math.floor(value / yearsPerPage) * yearsPerPage);
            }}
        >
            <PopoverTrigger asChild>
                <Button type="button" variant="outline" className={cn("tw-w-24 tw-tabular-nums", className)}>
                    {value}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="tw-w-64">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        disabled={!canGoPrev}
                        onClick={() => setRangeStart((prev) => prev - yearsPerPage)}
                    >
                        <ChevronLeft className="tw-h-4 tw-w-4" />
                    </Button>
                    <span className="tw-text-sm tw-font-medium">
                        {years[0]} - {years[years.length - 1]}
                    </span>
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        disabled={!canGoNext}
                        onClick={() => setRangeStart((prev) => prev + yearsPerPage)}
                    >
                        <ChevronRight className="tw-h-4 tw-w-4" />
                    </Button>
                </div>
                <div className="tw-grid tw-grid-cols-3 tw-gap-1.5">
                    {years.map((year) => {
                        const disabled = (minYear != null && year < minYear) || (maxYear != null && year > maxYear);
                        return (
                            <Button
                                key={year}
                                type="button"
                                variant={year === value ? "default" : "outline"}
                                size="sm"
                                disabled={disabled}
                                className="tw-tabular-nums"
                                onClick={() => {
                                    onChange?.(year);
                                    setOpen(false);
                                }}
                            >
                                {year}
                            </Button>
                        );
                    })}
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default YearPicker;
export { YearPicker };
